import * as R from 'ramda';
import { createPortal } from 'react-dom';
import { rectIntersection } from '@dnd-kit/core';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature template-inspection
import * as C from './constants';
import { validateComponent, emptySectionError } from './validation';
import { inspectionTemplateSettings } from './settings/templates-settings';
import vehicleTypesConfig from './settings/vehicle-configs/vehicle-types-config';
//////////////////////////////////////////////////

const VT = R.prop('valueTypeMap', C);

const isVehicleSection = (type) => R.includes(type, C.VEHICLE_SECTION_TYPES);

const getActiveVehicleComponent = (section) => {
  const { type, activeVehicleComponentType } = section;


  const sectionConfig = R.prop(type, vehicleTypesConfig);
  const vehicleComponentTypes = R.prop('vehicleComponentTypes', sectionConfig);

  return R.propOr({}, activeVehicleComponentType, vehicleComponentTypes);
};

const getComponentWithOptionValues = (component) => {
  const options = R.prop('options', component);

  if (G.isNilOrEmpty(options)) return component;

  return {
    ...component,
    options: R.map((option) => {
      const {
        getValue,
        valueType = VT.value,
      } = option;

      let value = R.prop(valueType, option);

      if (R.isNil(value)) {
        if (typeof getValue === 'function') value = getValue();
      }

      return R.omit(['getValue'], { ...option, [valueType]: value });
    }, options),
  };
};

const reorderList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

const renderDraggable = (draggableProps, element) => {
  if (R.pathEq('fixed', ['style', 'position'], draggableProps)) {
    return createPortal(element, document.body);
  }

  return element;
};

const templateNesting = ['sections', 'components'];

const isTemplateInvalid = (currentTemplate) => {
  let success = true;
  let updateTemplate = false;

  const template = R.clone(currentTemplate);

  const validate = (data, level) => {
    if (R.equals(level, 2)) {
      const error = validateComponent(data);

      if (G.isNotNilAndNotEmpty(error)) {
        success = false;

        // eslint-disable-next-line no-param-reassign
        data.errorMessage = error;
      } else if (data.errorMessage) {
        updateTemplate = true;

        // eslint-disable-next-line no-param-reassign
        delete data.errorMessage;
      }
    }

    if (G.notEquals(R.prop('nameStatus', data), C.INSPECTION_NAME_STATUS_FILLED)) {
      success = false;
    } else {
      const nestedField = R.prop(level, templateNesting);

      if (R.not(nestedField)) return;

      const nestedList = R.prop(nestedField, data);

      if (G.isNotNilAndNotEmpty(nestedList)) {
        R.forEach((item) => validate(item, R.inc(level)), nestedList);

         // eslint-disable-next-line no-param-reassign
        if (R.equals(level, 1)) delete data.errorMessage;
      } else {
        success = false;

        // eslint-disable-next-line no-param-reassign
        if (R.equals(level, 1)) data.errorMessage = emptySectionError;
      }
    }
  };

  validate(template, 0);

  return G.ifElse(R.and(success, R.not(updateTemplate)), false, template);
};

const remapSequenceByIndex = (currentTemplate) => {
  const template = R.clone(currentTemplate);

  const remap = (entity, level) => {
    const nestedField = R.prop(level, templateNesting);

    if (R.not(nestedField)) return;

    const nestedList = R.prop(nestedField, entity);

    if (G.isNotNilAndNotEmpty(nestedList)) {
      G.mapIndexed((item, i) => {
        // eslint-disable-next-line no-param-reassign
        item.sequence = i;
        // eslint-disable-next-line no-param-reassign
        item.expanded = false;

        remap(item, R.inc(level));
      }, nestedList);
    }
  };

  remap(template, 0);

  return template;
};

export const filterProps = R.mapObjIndexed(
  ({ name, options, collection, placeholder, filterType = 'string' }, value) => {
    let optionsToUse = options;

    if (G.isFunction(options)) optionsToUse = options();

    return ({
      value,
      collection,
      options: optionsToUse,
      [GC.FIELD_TYPE]: filterType,
      placeholder: G.ifElse(
        R.and(R.equals(filterType, 'selectMultiple'), R.isNil(placeholder)),
        { text: 'Choose a status', key: 'titles:choose-a-status' },
        placeholder,
      ),
      [GC.FIELD_NAME]: G.ifElse(
        G.isArray(name),
        // eslint-disable-next-line max-len
        `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}${G.ifElse(R.gt(R.length(name), 2), `: ${G.getWindowLocale(name[2])}`, '')}`,
        G.getWindowLocale(name, '...'),
      ),
    });
  },
  inspectionTemplateSettings,
);

const getSelectedItemGuids = (list) => R.pluck(GC.FIELD_GUID, R.filter(R.propEq(true, 'selected'), list));

const getInspectionTypeOptions = () => R.map((type) => ({
  value: type,
  label: G.getEnumLocale(type),
}), C.INSPECTION_TYPES);

const customCollisionDetectionAlgorithm = R.curry((height, args) => {
  const { collisionRect } = args;

  const modifiedCollisionRect = {
    ...collisionRect,
    height,
    originalHeight: collisionRect.height,
  };

  return rectIntersection({
    ...args,
    collisionRect: modifiedCollisionRect,
  });
});

const restrictToContainerBounds = R.curry((
  adjustHeight,
  { transform, activeNodeRect, containerNodeRect },
) => {
  if (!activeNodeRect || !containerNodeRect) {
    return transform;
  }

  const minY = containerNodeRect.top - activeNodeRect.top;
  const maxY = containerNodeRect.height - adjustHeight; // this part isn't correct for the expanded items

  const clampedY = Math.min(Math.max(transform.y, minY), maxY);

  return {
    ...transform,
    y: clampedY,
  };
});

export {
  reorderList,
  renderDraggable,
  isVehicleSection,
  isTemplateInvalid,
  remapSequenceByIndex,
  getSelectedItemGuids,
  restrictToContainerBounds,
  getInspectionTypeOptions,
  getActiveVehicleComponent,
  getComponentWithOptionValues,
  customCollisionDetectionAlgorithm,
};
